/**
 * DriverFuelCard — « ai-je une carte carburant, et qu'est-ce qu'elle paie ? »
 *
 * Demande JGS du 2026-09-14 : à la pompe, l'agent ne veut pas le dossier de la
 * carte, il veut la reconnaître dans son portefeuille et savoir si elle passera.
 * D'où quatre faits et rien d'autre : le fournisseur, les quatre derniers
 * chiffres, l'état, la restriction.
 *
 * ⚠️ RIEN DE SENSIBLE À L'ÉCRAN — jamais le numéro entier, jamais le code
 * confidentiel. Le composant ne montre que les quatre derniers chiffres, même
 * si le serveur lui en envoyait davantage : un écran de conducteur se consulte
 * en public, devant une pompe.
 *
 * PAS D'ÉTAT VIDE THÉÂTRAL : un agent sans carte n'a pas besoin d'apprendre
 * qu'il pourrait en avoir une. Sans carte, le bloc n'existe pas.
 */
import React from 'react';
import { CreditCard } from 'lucide-react';
import { isFlagOn } from '../../../utils/dbFlags.js';

/** Les états rendus par le serveur (`fuel_card.status`). Tout autre code s'affiche tel quel. */
const KNOWN_STATUSES = ['active', 'blocked', 'expired', 'cancelled'];

const TONES = {
  active: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  blocked: 'bg-red-50 text-red-800 border-red-200',
  expired: 'bg-amber-50 text-amber-900 border-amber-200',
  cancelled: 'bg-gray-100 text-gray-700 border-gray-200',
};

/**
 * Les quatre derniers chiffres, quelle que soit la forme reçue. Une carte sans
 * numéro lisible ne fabrique pas de faux chiffres : elle n'en montre aucun.
 */
export function lastFour(card) {
  const raw = card?.last4 ?? card?.card_number_last4 ?? card?.card_number;
  const digits = String(raw ?? '').replace(/\D/g, '');
  return digits.length >= 4 ? digits.slice(-4) : null;
}

/** Les carburants autorisés, quelle que soit la forme stockée (tableau, JSON, liste séparée par des virgules). */
export function fuelTypeList(value) {
  if (Array.isArray(value)) return value.filter(Boolean).map(String);
  if (typeof value !== 'string' || !value.trim()) return [];
  const text = value.trim();
  if (text.startsWith('[')) {
    try {
      const parsed = JSON.parse(text);
      return Array.isArray(parsed) ? parsed.filter(Boolean).map(String) : [];
    } catch {
      return [];
    }
  }
  return text.split(',').map((s) => s.trim()).filter(Boolean);
}

function FuelCardRow({ card, t, fmt }) {
  const status = KNOWN_STATUSES.includes(card.status) ? card.status : null;
  const digits = lastFour(card);
  const fuels = fuelTypeList(card.allowed_fuel_types);
  const provider = card.provider_name || card.provider || null;

  return (
    <li className="rounded-lg border border-gray-200 bg-white p-3">
      <div className="flex items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-600">
          <CreditCard className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="flex flex-wrap items-baseline gap-x-2">
            <span className="font-semibold text-gray-900">{provider || t('poolDriver.fuelCard.unknownProvider')}</span>
            {digits && <span className="font-mono text-sm text-gray-600">{t('poolDriver.fuelCard.masked', { last4: digits })}</span>}
          </p>
          <p className="mt-1">
            <span className={`inline-block rounded border px-1.5 py-0.5 text-xs font-medium ${TONES[status] || TONES.cancelled}`}>
              {status ? t(`poolDriver.fuelCard.status.${status}`) : card.status || t('poolDriver.fuelCard.status.unknown')}
            </span>
          </p>
          <ul className="mt-1.5 space-y-0.5 text-xs text-gray-600">
            {card.vehicle_plate && <li>{t('poolDriver.fuelCard.onVehicle', { plate: card.vehicle_plate })}</li>}
            {fuels.length > 0 && <li>{t('poolDriver.fuelCard.allowedFuel', { types: fuels.join(', ') })}</li>}
            {card.monthly_limit != null && (
              <li>{t('poolDriver.fuelCard.monthlyLimit', { amount: fmt.money(card.monthly_limit, 'EUR') })}</li>
            )}
            {card.expiry_date && <li>{t('poolDriver.fuelCard.expiresOn', { date: fmt.date(card.expiry_date) })}</li>}
            {isFlagOn(card.pin_required) && <li>{t('poolDriver.fuelCard.pinRequired')}</li>}
          </ul>
        </div>
      </div>
    </li>
  );
}

/**
 * @param {Array} [cards] - les cartes du conducteur, telles que le serveur les rend
 */
function DriverFuelCard({ cards, t, fmt }) {
  const list = (cards || []).filter(Boolean);
  if (!list.length) return null;

  return (
    <section className="space-y-2">
      <h3 className="text-sm font-semibold text-gray-900">{t('poolDriver.fuelCard.title')}</h3>
      <ul className="space-y-2">
        {list.map((card, i) => <FuelCardRow key={card.id ?? i} card={card} t={t} fmt={fmt} />)}
      </ul>
    </section>
  );
}

export default DriverFuelCard;
